import {
  DELIVERY_REGION,
  FREE_DELIVERY_FROM_PRICE,
  REGIONAL_FREE_DELIVERY_FROM_PRICE,
  ZIPCODES_ENKHUIZEN,
  ZIPCODES_STEDE_BROEC,
  ZIPCODES_WEST_FRIESLAND,
} from "../constants/DeliveryConstants";
import { COUNTRY_NL } from "../constants/CountryConstants";

export const parseZipPrefix = (zip) => {
  if (!zip) return null;
  const match = String(zip).match(/\d{4}/);
  return match ? parseInt(match[0], 10) : null;
};

/**
 * Valt dit adres buiten West-Friesland?
 *
 * Deze vlag staat op elke order (`landelijk`) en wordt door de `api` en
 * `fulfilment-cms` gelezen. Een onbekende postcode telt hier als landelijk; wie
 * het onderscheid "nog onbekend" nodig heeft (de verzendkosten), gebruikt
 * `deliveryRegionFor`.
 */
export const isLandelijk = (zip) => {
  const prefix = parseZipPrefix(zip);
  if (!prefix) return true; // treat unknown as landelijk until specified
  const localZips = new Set([
    ...ZIPCODES_ENKHUIZEN,
    ...ZIPCODES_STEDE_BROEC,
    ...ZIPCODES_WEST_FRIESLAND,
  ]);
  return !localZips.has(prefix);
};

/**
 * In welke bezorgregio valt dit adres (HVC-83)?
 *
 * - `regional`: een Nederlandse postcode in Enkhuizen, Stede Broec of de rest
 *   van West-Friesland. Daar rijdt de eigen bus.
 * - `national`: elk ander Nederlands adres, en elk adres buiten Nederland
 *   (Belgie is nooit regionaal).
 * - `null`: nog geen (bruikbare) Nederlandse postcode. Dan is er nog geen tarief
 *   en vraagt de winkelwagen om de postcode, zoals voor SooCool.
 *
 * @param {{zip?: string|number, country?: string}} [values] Het bezorgadres.
 *   Zonder land geldt Nederland: zo staat het in de checkout en in de
 *   postcodemodal.
 */
export const deliveryRegionFor = (values) => {
  const country = values?.country || COUNTRY_NL;
  if (country !== COUNTRY_NL) return DELIVERY_REGION.NATIONAL;
  const prefix = parseZipPrefix(values?.zip);
  if (!prefix) return null;
  return isLandelijk(prefix) ? DELIVERY_REGION.NATIONAL : DELIVERY_REGION.REGIONAL;
};

/**
 * Wordt dit product diepvries bezorgd? Sinds SooCool: altijd.
 *
 * Er was een regionale uitzondering - binnen West-Friesland reed Het
 * Vleescentrum zelf met gekoelde wagens, dus daar ging alleen mee wat het
 * product zelf als `frozen` had staan. Dat eigen transport is gestopt en SooCool
 * houdt de hele order van A tot Z bevroren, dus de uitzondering bestaat niet
 * meer en het productveld zegt niets meer over de bezorging.
 *
 * `null` bij een onbekende postcode blijft: dan is er nog geen bezorgwijze
 * gekozen en horen er ook geen labels te staan.
 */
export const effectiveFrozenForZip = (productFrozen, zip) => {
  const prefix = parseZipPrefix(zip);
  if (!prefix) return null; // unknown yet => do not show tags
  return true;
};

/**
 * De gratis-bezorgdrempel: EUR 100 in West-Friesland (de eigen bus), EUR 150
 * daarbuiten. Zonder bekende postcode de landelijke drempel, zodat een USP nooit
 * meer belooft dan voor de meeste klanten geldt.
 */
export const getFreeDeliveryFromPrice = (zip, country) => {
  return deliveryRegionFor({ zip, country }) === DELIVERY_REGION.REGIONAL
    ? REGIONAL_FREE_DELIVERY_FROM_PRICE
    : FREE_DELIVERY_FROM_PRICE;
};
